import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiLock, FiTruck } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/checkout.css";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

const SHIPPING_FEE = 9.99;
const availableCountries = [
  "Australia",
  "Austria",
  "Belgium",
  "Canada",
  "China",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Hong Kong",
  "Ireland",
  "Italy",
  "Japan",
  "Macao",
  "Malaysia",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Portugal",
  "Singapore",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
];

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  country: "Hong Kong",
  shippingMethod: "Standard tracked shipping — $9.99",
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const orderItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = orderItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const total = subtotal + SHIPPING_FEE;

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const requiredFields = [
      "email",
      "firstName",
      "lastName",
      "address",
      "city",
      "region",
      "postalCode",
      "country",
      "shippingMethod",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field].trim()) {
        nextErrors[field] = "This field is required.";
      }
    });

    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      nextErrors.email = "Enter a valid email address.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalid = Object.keys(nextErrors)[0];
      document.querySelector(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    const randomDigits = Array.from(crypto.getRandomValues(new Uint32Array(4)))
      .map((value) => String(value).padStart(10, "0"))
      .join("")
      .slice(0, 30);
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo: randomDigits,
      amount: total.toFixed(2),
      currency: "USD",
      email: formData.email.trim(),
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: orderItems[0].product.name,
    });

    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (orderItems.length === 0) {
    return (
      <main className="checkout-empty">
        <div>
          <h1>Your cart is empty.</h1>
          <p>Add something inspiring before continuing to checkout.</p>
          <Link to="/shop">Browse new releases</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-page__header">
        <span>ioomparade</span>
        <h1>Checkout</h1>
        <p>
          <FiLock aria-hidden="true" /> Secure payment
        </p>
      </header>

      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={handleSubmit} noValidate>
          <div className="checkout-notice" role="note">
            <FiLock aria-hidden="true" />
            <div>
              <strong>Secure checkout</strong>
              <p>
                Enter your contact and delivery details. Payment is completed
                securely after you select Pay now; no card details are entered here.
              </p>
            </div>
          </div>

          <fieldset>
            <legend>Contact</legend>
            <div className="checkout-field checkout-field--full">
              <label htmlFor="checkout-email">Email address</label>
              <input
                id="checkout-email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={updateField}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "checkout-email-error" : undefined}
              />
              {errors.email && (
                <span className="checkout-error" id="checkout-email-error">
                  {errors.email}
                </span>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend>Delivery</legend>
            <div className="checkout-fields">
              <div className="checkout-field">
                <label htmlFor="checkout-first-name">First name</label>
                <input
                  id="checkout-first-name"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.firstName)}
                />
                {errors.firstName && (
                  <span className="checkout-error">{errors.firstName}</span>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-last-name">Last name</label>
                <input
                  id="checkout-last-name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.lastName)}
                />
                {errors.lastName && (
                  <span className="checkout-error">{errors.lastName}</span>
                )}
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-company">Company (optional)</label>
                <input
                  id="checkout-company"
                  name="company"
                  autoComplete="organization"
                  value={formData.company}
                  onChange={updateField}
                />
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-address">Address</label>
                <input
                  id="checkout-address"
                  name="address"
                  autoComplete="street-address"
                  value={formData.address}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.address)}
                />
                {errors.address && (
                  <span className="checkout-error">{errors.address}</span>
                )}
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-apartment">
                  Apartment, suite, etc. (optional)
                </label>
                <input
                  id="checkout-apartment"
                  name="apartment"
                  autoComplete="address-line2"
                  value={formData.apartment}
                  onChange={updateField}
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-city">City</label>
                <input
                  id="checkout-city"
                  name="city"
                  autoComplete="address-level2"
                  value={formData.city}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.city)}
                />
                {errors.city && (
                  <span className="checkout-error">{errors.city}</span>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-region">State / province</label>
                <input
                  id="checkout-region"
                  name="region"
                  autoComplete="address-level1"
                  value={formData.region}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.region)}
                />
                {errors.region && (
                  <span className="checkout-error">{errors.region}</span>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-postal">Postal code</label>
                <input
                  id="checkout-postal"
                  name="postalCode"
                  autoComplete="postal-code"
                  value={formData.postalCode}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.postalCode)}
                />
                {errors.postalCode && (
                  <span className="checkout-error">{errors.postalCode}</span>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  id="checkout-country"
                  name="country"
                  autoComplete="country-name"
                  value={formData.country}
                  onChange={updateField}
                >
                  {availableCountries.map((country) => (
                    <option key={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Shipping method</legend>
            <label className="checkout-shipping" htmlFor="checkout-shipping">
              <FiTruck aria-hidden="true" />
              <span>
                <strong>Carrier option</strong>
                <small>
                  Fixed tracked delivery fee.
                </small>
              </span>
              <select
                id="checkout-shipping"
                name="shippingMethod"
                value={formData.shippingMethod}
                onChange={updateField}
              >
                <option>Standard tracked shipping — $9.99</option>
              </select>
            </label>
          </fieldset>

          <button className="checkout-submit" type="submit">
            Pay now — {formatMoney(total)}
          </button>
          <div className="checkout-links">
            <Link to="/cartpage">Return to cart</Link>
            <Link to="/shipping">Shipping information</Link>
            <Link to="/returns">Refund policy</Link>
          </div>
        </form>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <h2 id="checkout-summary-title">Your order</h2>
          <div className="checkout-summary__items">
            {orderItems.map((item) => (
              <article
                className="checkout-summary__item"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <div className="checkout-summary__image">
                  <img src={item.product.image} alt="" />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>
                    {[item.color, item.size].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <strong>
                  {formatMoney(Number(item.product.price) * item.quantity)}
                </strong>
              </article>
            ))}
          </div>
          <dl className="checkout-totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatMoney(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{formatMoney(SHIPPING_FEE)}</dd>
            </div>
            <div className="checkout-totals__total">
              <dt>Total</dt>
              <dd>
                <small>USD</small> {formatMoney(total)}
              </dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
